import React from "react";
import type { ApolloClient } from "@apollo/client";
import { useParams } from "react-router-dom";
import StepWizard from "components/StepWizard";

import { connect, type ConnectedProps } from "react-redux";
import { filter, find, get } from "lodash";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { push } from "store/actions/router";
import { hasAutoApprovalLinkRequestsFeature } from "store/selectors/employer";
import {
  getAllEmployerGroups,
  getLinkedEmployees,
  getPendingLinkedEmployees,
} from "actions/employerActions";
import type { RootState } from "types/state";
import type { EmployerGroup, EmployerLinkRequest } from "types/models";

import IconSpinner from "components/IconSpinner";
import StepInfo from "components/StepInfo";
import ReviewEmployeeLinkRequest from "./ReviewEmployeeLinkRequest";
import ApproveEmployeeLinkRequest from "./ApproveEmployeeLinkRequest";

const stepToLabels: Record<string, { subheader: string }> = {
  1: {
    subheader: "Here is a list of all employees requiring a review.",
  },
  2: {
    subheader:
      "Outlined below is a summary encompassing all current approvals, rejections, and existing employees associated with this plan.",
  },
};

const steps = [
  {
    index: 1,
    label: "Review Existing",
  },
  {
    index: 2,
    label: "Approve Changes",
  },
];

interface OwnProps {
  groupId?: string;
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const groupId = ownProps.groupId;

  const linkedEmployees = filter(state.employer.linkedEmployees, (employee) => {
    const employeeGroup = get(employee, "groupWithSchedule.group.id", "");
    return employeeGroup === groupId;
  });

  const hasAutoApprovalFeature = hasAutoApprovalLinkRequestsFeature(state);

  const pendingLinks = hasAutoApprovalFeature
    ? []
    : filter(state.employer.employerLinkRequests, (request) => {
        const requestGroupId = get(request, "group.id", "");
        return (
          (request as { status?: unknown }).status === "REQUEST_ACCESS" &&
          requestGroupId === groupId
        );
      });

  const group = find(state.employer.groups, { id: groupId });

  // state.employer slices are intentionally shallow-typed as `unknown`
  // (types/state.ts) because that layer is scheduled for deletion; cast at the
  // boundary to the domain models the (already-typed) Review/Approve consumers
  // expect.
  return {
    planId: state.employer.company.planId,
    employerLinkRequests: pendingLinks as unknown as EmployerLinkRequest[],
    linkedEmployees: linkedEmployees as unknown as EmployerLinkRequest[],
    group: group as EmployerGroup | undefined,
    groupId,
  };
};

const mapDispatchToProps = {
  push,
  getAllEmployerGroups,
  getPendingLinkedEmployees,
  getLinkedEmployees,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

// These step children declare `goToNamedStep` as a required prop and do not
// declare `stepName`, but StepWizard injects `goToNamedStep` (and reads
// `stepName`) on each child at runtime via cloneElement — so those props are
// genuinely absent at this JSX site. Alias to loose ComponentTypes at the
// boundary to mirror the original JS exactly without weakening the siblings'
// own typed Props (same pattern as AdminUserProfileWorkflow.tsx).
const ReviewStep = ReviewEmployeeLinkRequest as unknown as React.ComponentType<
  Record<string, unknown>
>;
const ApproveStep =
  ApproveEmployeeLinkRequest as unknown as React.ComponentType<
    Record<string, unknown>
  >;

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  initialFetching: boolean;
  rejectedPlans: Record<string, boolean>;
  approvedPlans: Record<string, boolean>;
  activeStep: string | number;
}

class EmployeeLinkRequestWorkflow extends React.Component<Props, State> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
      rejectedPlans: {},
      approvedPlans: {},
      activeStep: "1",
    };
  }

  async componentDidMount() {
    await Promise.all([
      this.props.getPendingLinkedEmployees(
        this.props.client as ApolloClient<object>
      ),
      this.props.getLinkedEmployees(this.props.client as ApolloClient<object>),
      this.props.getAllEmployerGroups(
        this.props.client as ApolloClient<object>
      ),
    ]);
    this.setState({ initialFetching: false });
  }

  _onStepChange = ({ activeStep }: { activeStep: number }) => {
    this.setState({ activeStep });
  };

  _setApproved = (approvedIds: Record<string, boolean>) => {
    this.setState({ approvedPlans: approvedIds });
  };

  _setRejected = (rejectedIds: Record<string, boolean>) => {
    this.setState({ rejectedPlans: rejectedIds });
  };

  render() {
    const activeSectionLabels = stepToLabels[this.state.activeStep];

    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    if (!this.props.group) {
      return null;
    }

    return (
      <>
        <p className="steps-header">{activeSectionLabels.subheader}</p>
        <StepInfo steps={steps} activeStepNumber={this.state.activeStep} />
        <StepWizard
          onStepChange={this._onStepChange}
          isLazyMount={true}
          transitions={{
            enterRight: "",
            enterLeft: "",
            exitRight: "",
            exitLeft: "",
          }}
        >
          <ReviewStep
            group={this.props.group}
            stepName="existing"
            pendingLinkRequests={this.props.employerLinkRequests}
            rejectedPlans={this.state.rejectedPlans}
            approvedPlans={this.state.approvedPlans}
            setApproved={this._setApproved}
            setRejected={this._setRejected}
          />
          <ApproveStep
            group={this.props.group}
            stepName="approvals"
            planType={this.props.group.planType}
            linkedEmployees={this.props.linkedEmployees}
            pendingLinkRequests={this.props.employerLinkRequests}
            rejectedPlans={this.state.rejectedPlans}
            approvedPlans={this.state.approvedPlans}
            groupId={this.props.groupId}
          />
        </StepWizard>
      </>
    );
  }
}

const ConnectedEmployeeLinkRequestWorkflow = connector(
  withApollo<Props>(EmployeeLinkRequestWorkflow)
);

const EmployeeLinkRequestWorkflow_WithParams = (props: OwnProps) => {
  const { id: groupId } = useParams();
  return <ConnectedEmployeeLinkRequestWorkflow {...props} groupId={groupId} />;
};

export default EmployeeLinkRequestWorkflow_WithParams;
